import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClientError, type ApiClientErrorCode, type FurnitureListItem } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import {
  FurnitureSearchView,
  type FurnitureBrandOption,
  type FurnitureSearchState,
} from './FurnitureSearchView';

/**
 * Container Экрана 2.5.7 «Фурнитура и магазины — поиск» (ADR-0068, ADR-0005).
 *
 * Живёт на `/furniture/search`. Поля: Бренд (select) + Название (живой поиск,
 * debounce 300мс, минимум 2 символа). Данные — ТОЛЬКО через типизированный
 * `apiClient.furniture.*` (P-012, без прямого fetch). Бренд-опции — из
 * `apiClient.furniture.brands()` (ВСЕ distinct-бренды каталога, ADR-0077):
 * собирать их из `list({limit:100})` нельзя — каталог засеян по брендам подряд,
 * первые 100 строк = один бренд (баг прода). Data-driven, без хардкода.
 *
 * Список результатов — ТЕКСТОВЫЙ (решение Aibek поверх прототипа: без мини-картинок),
 * поэтому signed-URL фото для списка НЕ тянем; hero-фото тянет карточка (E6) сама.
 *
 * idle (query пуст) = скоуп-список (по выбранному бренду или весь). Тап по
 * подсказке → `/furniture/:globalId` (карточка, E6). Back=история (BottomBar, P-036).
 */
const DEFAULT_LIMIT = 50;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

function toApiErr(err: unknown): { code: ApiClientErrorCode; message: string } {
  // Не leak-аем String(err) (security): non-ApiClientError → пустой message.
  return err instanceof ApiClientError
    ? { code: err.code, message: err.message }
    : { code: 'INTERNAL_ERROR', message: '' };
}

export function FurnitureSearch() {
  const navigate = useNavigate();

  const [brand, setBrand] = useState('');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Опции бренда — ВСЕ distinct-бренды каталога с сервера (ADR-0077), отсортированы
  // по brandName на бэке. Грузим один раз на маунт. `FurnitureBrand{brand,brandName}`
  // → `FurnitureBrandOption{code,brandName}` (code = код бренда для value/match).
  const [brandOptions, setBrandOptions] = useState<readonly FurnitureBrandOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    apiClient.furniture.brands().then(
      (res) => {
        if (cancelled) return;
        setBrandOptions(res.brands.map((b) => ({ code: b.brand, brandName: b.brandName })));
      },
      () => {
        if (!cancelled) setBrandOptions([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const [items, setItems] = useState<readonly FurnitureListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);

  const trimmed = debounced.trim();
  // Поиск активен при ≥2 символах; иначе idle = скоуп-список (brand или весь).
  const nameForQuery = trimmed.length >= MIN_QUERY_LENGTH ? trimmed : undefined;
  const hasQuery = nameForQuery !== undefined;

  useEffect(() => {
    let cancelled = false;
    // Сброс stale-результатов при смене ключа запроса (brand/name): прежние items
    // относятся к ПРЕДЫДУЩЕму запросу и невалидны для нового. Без сброса гейт
    // `loading && items.length===0` подавляется ненулевыми stale-items, и на сетевой
    // латентности они показываются как «результаты» текущего поиска — мерцание
    // «выходит [чужое] и сразу пропадает». Сброс → корректный loading-стейт.
    setItems([]);
    setLoading(true);
    setError(null);
    apiClient.furniture
      .list({ brand: brand || undefined, name: nameForQuery, limit: DEFAULT_LIMIT })
      .then(
        (res) => {
          if (cancelled) return;
          setItems(res.items);
          setLoading(false);
        },
        (err: unknown) => {
          if (cancelled) return;
          setError(toApiErr(err));
          setLoading(false);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [brand, nameForQuery]);

  const state = useMemo<FurnitureSearchState>(() => {
    if (error) return 'error';
    if (loading && items.length === 0) return 'loading';
    if (items.length === 0) return hasQuery ? 'empty' : 'idle';
    return hasQuery ? 'success' : 'idle';
  }, [error, loading, items.length, hasQuery]);

  return (
    <FurnitureSearchView
      state={state}
      brandOptions={brandOptions}
      brand={brand}
      onBrandChange={setBrand}
      query={query}
      onQueryChange={setQuery}
      items={items}
      error={error ?? undefined}
      onItemClick={(globalId) => navigate(`/furniture/${encodeURIComponent(globalId)}`)}
    />
  );
}
